import { isInTelegram } from '../telegram.ts'
import { dismissKeyboard, isKeyboardField } from './keyboard.ts'

interface TouchPoint { identifier: number; clientX: number; clientY: number }
const SWIPE_PX = 40
const INTENT_PX = 10
const LONG_PRESS_MS = 400

/** Один палец, начавший движение вниз. Направление нельзя переиграть в том же жесте. */
export class KeyboardSwipe {
  private start: (TouchPoint & { time: number }) | null = null
  private moving = false

  begin(point: TouchPoint, time: number): void {
    // У native Touch координаты — prototype getters, object spread их теряет.
    this.start = { identifier: point.identifier, clientX: point.clientX, clientY: point.clientY, time }
    this.moving = false
  }

  cancel(): void { this.start = null }

  move(point: TouchPoint, time: number): boolean {
    const start = this.start
    if (!start) return false
    const elapsed = time - start.time
    const dx = Math.abs(point.clientX - start.clientX)
    const dy = point.clientY - start.clientY
    if (point.identifier !== start.identifier || elapsed < 0
      || (!this.moving && elapsed >= LONG_PRESS_MS)
      || dy <= -INTENT_PX || (dx >= INTENT_PX && dx > Math.abs(dy))) {
      this.cancel()
      return false
    }
    if (Math.max(dx, Math.abs(dy)) >= INTENT_PX) this.moving = true
    if (dy < SWIPE_PX || dy < dx * 1.5) return false
    this.cancel()
    return true
  }
}

const TEXT_INPUTS = new Set(['text', 'search', 'tel', 'url', 'email', 'password', 'number'])
const OWN_GESTURE = 'select, [role="toolbar"], video, audio, [role="slider"], [draggable="true"], [data-bar], [data-draw-row], [data-keyboard-swipe="ignore"]'

function writableField(element: Element | null): element is HTMLElement {
  return isKeyboardField(element) && element.isConnected
    && !element.matches('[readonly], :disabled, [inputmode="none" i]')
    && (element.tagName !== 'INPUT' || TEXT_INPUTS.has((element as HTMLInputElement).type))
}

function selectionSnapshot(doc: Document, field: HTMLElement) {
  const selection = doc.getSelection()
  const input = field.tagName === 'INPUT' || field.tagName === 'TEXTAREA'
    ? field as HTMLInputElement | HTMLTextAreaElement : null
  return {
    anchor: selection?.anchorNode ?? null,
    anchorOffset: selection?.anchorOffset ?? 0,
    focus: selection?.focusNode ?? null,
    focusOffset: selection?.focusOffset ?? 0,
    collapsed: selection?.isCollapsed ?? true,
    start: input?.selectionStart ?? null,
    end: input?.selectionEnd ?? null,
    direction: input?.selectionDirection ?? null,
  }
}
type SelectionSnapshot = ReturnType<typeof selectionSnapshot>

function sameSelection(a: SelectionSnapshot, b: SelectionSnapshot): boolean {
  return a.anchor === b.anchor && a.anchorOffset === b.anchorOffset
    && a.focus === b.focus && a.focusOffset === b.focusOffset && a.collapsed === b.collapsed
    && a.start === b.start && a.end === b.end && a.direction === b.direction
}

/**
 * Наблюдаем TouchEvents, не PointerEvents: браузер отменяет pointer при начале scroll.
 * Capture охватывает порталы и панели со stopPropagation. Свои drag-поверхности
 * обязаны ставить data-keyboard-swipe="ignore": в capture ещё не виден их preventDefault.
 */
export function installKeyboardSwipeDismiss(doc: Document = document): () => void {
  if (!isInTelegram()) return () => undefined
  const gesture = new KeyboardSwipe()
  let field: HTMLElement | null = null
  let selection: SelectionSnapshot | null = null
  const cancel = () => { gesture.cancel(); field = null; selection = null }
  const valid = () => field !== null && field === doc.activeElement && writableField(field)
    && selection !== null && sameSelection(selection, selectionSnapshot(doc, field))
  const onStart = (event: TouchEvent) => {
    cancel()
    const point = event.touches[0]
    const target = event.target instanceof Element ? event.target : null
    const active = doc.activeElement
    if (event.touches.length !== 1 || !point || !target || target.closest(OWN_GESTURE)
      || !writableField(active)) return
    // Своё поле тоже прокручивается со скрытием клавиатуры. Чужое поле сначала
    // должно получить фокус обычным касанием: этот жест не завершает новый ввод.
    if (target.closest('input, textarea, [contenteditable], [role="textbox"]') && !active.contains(target)) return
    const snapshot = selectionSnapshot(doc, active)
    if (!snapshot.collapsed || snapshot.start !== snapshot.end) return
    field = active
    selection = snapshot
    gesture.begin(point, event.timeStamp)
  }
  const move = (point: TouchPoint | undefined, time: number) => {
    if (!point || !valid()) { cancel(); return }
    if (gesture.move(point, time)) {
      cancel()
      dismissKeyboard()
    }
  }
  const onMove = (event: TouchEvent) => {
    if (event.touches.length !== 1) { cancel(); return }
    move(event.touches[0], event.timeStamp)
  }
  const onEnd = (event: TouchEvent) => {
    if (event.touches.length === 0 && event.changedTouches.length === 1) move(event.changedTouches[0], event.timeStamp)
    cancel()
  }
  const onSelection = () => { if (field && !valid()) cancel() }
  const listeners = [
    ['touchstart', onStart], ['touchmove', onMove], ['touchend', onEnd], ['touchcancel', cancel],
    ['focusin', cancel], ['focusout', cancel], ['selectionchange', onSelection], ['selectstart', cancel],
    ['visibilitychange', cancel],
  ] as const
  for (const [type, listener] of listeners) doc.addEventListener(type, listener as EventListener, { capture: true, passive: true })
  doc.defaultView?.addEventListener('blur', cancel)
  return () => {
    cancel()
    for (const [type, listener] of listeners) doc.removeEventListener(type, listener as EventListener, true)
    doc.defaultView?.removeEventListener('blur', cancel)
  }
}
